import { PostData } from "./FetchData";

const MS1 = "ms1";

export const LLMS_TXT_JOB_POLL_MS = 2000;

function parseMs1Response(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "failure") {
		const message =
			typeof res.message === "string" ? res.message : fallbackMessage;
		return { ok: false, message, data: res.data ?? null };
	}
	if (res?.status !== "success") {
		const message =
			typeof res?.message === "string"
				? res.message
				: res?.message?.message || fallbackMessage;
		return { ok: false, message, data: null };
	}
	return { ok: true, message: res.message ?? null, data: res.data ?? null };
}

function normalizeJobId(jobId) {
	return Number.isFinite(Number(jobId)) ? Number(jobId) : jobId;
}

export function isLlmsTxtJobComplete(status) {
	const s = String(status || "").toLowerCase();
	return s === "completed";
}

export function isLlmsTxtJobFailed(status, errorMessage) {
	const s = String(status || "").toLowerCase();
	if (s === "failed" || s === "error") return true;
	return errorMessage != null && errorMessage !== "";
}

export function isLlmsTxtJobTerminal(status) {
	return isLlmsTxtJobComplete(status) || isLlmsTxtJobFailed(status);
}

export async function submitLlmsTxtJob(websiteId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", job: null };
	}
	const res = await PostData(MS1, "api/v1/website/llmsTxtSubmitJob", {
		websiteId: Number(websiteId),
	});
	const parsed = parseMs1Response(res, "Failed to submit llms.txt job");
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

export async function listLlmsTxtJobs(websiteId, { skip = 0, limit = 20 } = {}) {
	if (websiteId == null) {
		return {
			ok: false,
			message: "Missing website id",
			jobs: [],
			total: 0,
			hasMore: false,
			skip: 0,
			limit,
		};
	}
	const res = await PostData(MS1, "api/v1/website/llmsTxtJobs", {
		websiteId: Number(websiteId),
		skip,
		limit,
	});
	const parsed = parseMs1Response(res, "Failed to load llms.txt jobs");
	if (!parsed.ok) {
		return {
			ok: false,
			message: parsed.message,
			jobs: [],
			total: 0,
			hasMore: false,
			skip,
			limit,
		};
	}
	const jobs = Array.isArray(parsed.data?.jobs) ? parsed.data.jobs : [];
	const total =
		parsed.data?.total != null ? Number(parsed.data.total) : jobs.length;
	return {
		ok: true,
		message: parsed.message,
		jobs,
		total,
		hasMore: Boolean(parsed.data?.has_more),
		skip: parsed.data?.skip ?? skip,
		limit: parsed.data?.limit ?? limit,
	};
}

export async function getLlmsTxtJobStatus(websiteId, jobId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", status: null };
	}
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", status: null };
	}
	const res = await PostData(MS1, "api/v1/website/llmsTxtJobStatus", {
		websiteId: Number(websiteId),
		jobId: normalizeJobId(jobId),
	});
	const parsed = parseMs1Response(res, "Failed to load job status");
	if (!parsed.ok) return { ok: false, message: parsed.message, status: null };
	return { ok: true, message: parsed.message, status: parsed.data };
}

export async function getLlmsTxtJob(websiteId, jobId, { includeOutput = false } = {}) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", job: null };
	}
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", job: null };
	}
	const res = await PostData(MS1, "api/v1/website/llmsTxtJob", {
		websiteId: Number(websiteId),
		jobId: normalizeJobId(jobId),
		includeOutput: Boolean(includeOutput),
	});
	const parsed = parseMs1Response(res, "Failed to load llms.txt job");
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

export async function cancelLlmsTxtJob(websiteId, jobId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", job: null };
	}
	if (jobId == null || jobId === "") {
		return { ok: false, message: "Missing job id", job: null };
	}
	const res = await PostData(MS1, "api/v1/website/llmsTxtCancelJob", {
		websiteId: Number(websiteId),
		jobId: normalizeJobId(jobId),
	});
	const parsed = parseMs1Response(res, "Failed to cancel llms.txt job");
	if (!parsed.ok) return { ok: false, message: parsed.message, job: null };
	return { ok: true, message: parsed.message, job: parsed.data };
}

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function pollLlmsTxtJob(
	websiteId,
	jobId,
	{ intervalMs = LLMS_TXT_JOB_POLL_MS, onTick } = {}
) {
	if (websiteId == null || jobId == null || jobId === "") {
		return { ok: false, message: "Missing website or job id", status: null };
	}

	for (;;) {
		const result = await getLlmsTxtJobStatus(websiteId, jobId);
		if (!result.ok) {
			return { ok: false, message: result.message, status: null };
		}

		const statusData = result.status;
		if (typeof onTick === "function") onTick(statusData);

		if (isLlmsTxtJobFailed(statusData?.status, statusData?.error)) {
			return {
				ok: false,
				message: statusData?.error || "Job failed",
				status: statusData,
			};
		}

		if (isLlmsTxtJobComplete(statusData?.status)) {
			return { ok: true, message: null, status: statusData };
		}

		await sleep(intervalMs);
	}
}
